function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const PagedList = require('../entities/paged-list');

function _invoke(body, then) {
  var result = body();

  if (result && result.then) {
    return result.then(then);
  }

  return then(result);
}

const Address = require('../entities/address');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const placeCache = require('./place-id-cache');

const {
  strinct,
  strList
} = require('../utils/serialize');

const idField = ['id'];
const fields = ['id', 'suite', 'streetAddress', 'street', 'city', 'state', 'country', 'osmUid'];
const searchFields = ['id', 'osmUid', 'googlePlaceId', 'suite', 'building', 'streetAddress', 'street', 'postalcode', 'locality', 'neighbourhood', 'city', 'county', 'state', 'region', 'country', 'centre', 'area', 'timezone'];

const parse = json => new Address(json);

class LegacyAddressesRepo {
  constructor(client
  /*: ApiClient*/
  ) {
    this.client = client;
  }

  search(...args) {
    // Alias until old references to "search" (meaning "discover") are removed
    return this.discover(...args);
  }

  discover({
    /*: {
        q: string,
        street: string,
        city: string,
        postalcode: string,
        country: string
      }*/

    q,
    street,
    city,
    postalcode,
    country
  } = {})
  /*: Promise<PagedList<Address>>*/
  {
    const params = {
      q,
      street,
      city,
      postalcode,
      country
    };
    const req = new this.client.Request('/addresses/discover', params);
    req.parse = Address.fromJson;
    return this.client.get(req) // FIXME: Remove this when the API filters addresses without ids...
    .then(pagedList => new PagedList(pagedList.items.filter(address => address.osmUid !== 'undefined undefined'), pagedList.page));
  } // TODO: Rename to "search" and remove existing "search" alias


  searchPersisted({
    /*: {
        addressId?: string,
        building?: string,
        street?: string,
        neighbourhood?: string,
        city: ? string,
        county?: string,
        state?: string,
        country?: string,
        query?: string,
        limit?: string,
        offset?: string
      }*/

    addressId,
    building,
    street,
    neighbourhood,
    city,
    county,
    state,
    country,
    query,
    limit,
    offset
  }) {
    const params = {
      addressId,
      building,
      street,
      neighbourhood,
      city,
      county,
      state,
      country,
      q: query,
      limit,
      offset
    };
    const req = new this.client.Request('/addresses/search', params, searchFields);
    req.parse = parse;
    return this.client.get(req);
  }

  persist(address
  /*: Address*/
  )
  /*: Promise<Address>*/
  {
    if (address.id) return Promise.resolve(address);
    const cachedAddress = placeCache.getItem(address.osmUid);
    if (cachedAddress) return Promise.resolve(cachedAddress);
    const params = {
      token: address.token
    };
    const req = new this.client.Request(`/addresses`, params, idField);

    req.parse = json => new Address(json);

    return this.client.post(req).then(persistedAddress => {
      placeCache.setItem(address.osmUid, persistedAddress);
      return persistedAddress;
    });
  }

  details(id
  /*: string*/
  )
  /*: Promise<Address>*/
  {
    const req = new this.client.Request(`/addresses/${id}`, undefined, fields);

    req.parse = json => new Address(json);

    return this.client.get(req);
  }

  sync(addressId
  /*: String*/
  , token
  /*: String*/
  )
  /*: Promise<Address>*/
  {
    const _this = this;

    return _call(function () {
      if (!addressId) throw new Error('Cannot update non-persisted Address');
      return _await(_invoke(function () {
        if (!token) {
          return _await(_this.details(addressId), function (_this$details) {
            const osmUid = _this$details.osmUid;
            return _await(_this.search({
              q: osmUid
            }), function (_this$search) {
              token = _this$search.items[0].token;
            }); // eslint-disable-line no-param-reassign
          });
        }
      }, function () {
        if (!token) throw new Error(`Unable to resolve updated token for address ${addressId}`);
        const req = new _this.client.Request(`/addresses/${addressId}`, {
          token
        }, idField);
        req.parse = parse;
        return _this.client.post(req);
      }));
    });
  }

  generateToken(address
  /*: Object*/
  )
  /*: Promise<string>*/
  {
    const params = {
      country: address.country,
      state: address.state,
      city: address.city,
      centre: strList([address.centre.long, address.centre.lat]),
      googlePlaceId: address.googlePlaceId,
      suite: strinct(address.suite),
      building: address.building,
      streetAddress: address.streetAddress,
      street: address.street,
      postalcode: address.postalcode,
      locality: address.locality,
      neighbourhood: address.neighbourhood,
      county: address.county,
      region: address.region,
      area: address.area,
      nameShort: address.nameShort
    };
    const path = `/addresses/token`;
    const req = new this.client.Request(path, params);
    return this.client.get(req).then(({
      token
    }) => token);
  }

  create(address
  /*: Object*/
  )
  /*: Promise<Address>*/
  {
    const _this2 = this;

    return _call(function () {
      // HACK: Search for existing exact match and use that if possible
      // FIXME: Remove this when addresses are reworked and we can have multiple with the same properties
      return _await(_this2.searchPersisted({
        country: address.country,
        city: address.city,
        street: address.street,
        state: address.state
      }).then(_this2.client.pagination.loadRest), function (existingAddresses) {
        // We have to check streetAddress and suite because we can't search by those (for some reason)
        const exactMatch = existingAddresses.items.find(existingAddress => {
          const sameStreetAddress = existingAddress.streetAddress === address.streetAddress;
          /* eslint-disable-next-line eqeqeq */
          // treat null and undefined as equal

          const sameSuite = existingAddress.suite == address.suite;
          const samePostalCode = existingAddress.postalcode === address.postalcode;
          const sameLat = existingAddress.centre.lat === address.centre.lat;
          const sameLong = existingAddress.centre.long === address.centre.long;
          return sameStreetAddress && sameSuite && samePostalCode && sameLong && sameLat;
        });
        return exactMatch ? exactMatch : _await(_this2.generateToken(address), function (token) {
          const req = new _this2.client.Request(`/addresses`, {
            token
          }, fields);
          req.parse = parse;
          return _this2.client.post(req);
        });
      });
    });
  }

}

module.exports = LegacyAddressesRepo;